<template>
  <div class="kpi-cards-container">
    <div
      v-for="kpi in computedKpis"
      :key="kpi.status"
      class="kpi-card"
      :class="{ 'is-active': selectedStatus === kpi.status }"
      @click="toggleStatus(kpi.status)"
    >
      <div class="kpi-icon" :style="{ background: kpi.bgColor, color: kpi.color }">
        <feather-icon :icon="kpi.icon" size="18" />
      </div>
      <div class="kpi-content">
        <span class="kpi-count">{{ kpi.count }}</span>
        <span class="kpi-label">{{ kpi.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KpiCards',
  props: {
    orders: {
      type: Array,
      default: () => [],
    },
    statusSummary: {
      type: Array,
      default: () => [],
    },
    selectedStatus: {
      type: String,
      default: null,
    },
  },
  computed: {
    computedKpis() {
      const statusMap = this.aggregateByStatus();
      const sorted = Object.entries(statusMap)
        .sort((a, b) => b[1] - a[1]);

      return sorted.map(([status, count]) => ({
        status,
        label: this.normalizeLabel(status),
        count,
        ...this.getStatusStyle(status),
      }));
    },
  },
  methods: {
    aggregateByStatus() {
      if (Array.isArray(this.statusSummary) && this.statusSummary.length > 0) {
        const map = {};
        this.statusSummary.forEach((item) => {
          const rawStatus = item?.pedidositcomercial || item?.pedidoSitComercial || item?.status || 'SEM STATUS';
          const normalized = String(rawStatus).toUpperCase().trim();
          const total = Number(item?.Total ?? item?.total ?? 0);
          map[normalized] = (map[normalized] || 0) + (Number.isNaN(total) ? 0 : total);
        });
        return map;
      }
      const map = {};
      this.orders.forEach((order) => {
        const rawStatus = order.pedidoSitSales || order.pedidoSitComercial || order.situacao || 'SEM STATUS';
        const normalized = String(rawStatus).toUpperCase().trim();
        map[normalized] = (map[normalized] || 0) + 1;
      });
      return map;
    },
    normalizeLabel(status) {
      const labelMap = {
        DIGITADO: 'Digitados',
        INTEGRADO: 'Integrados',
        APROVADO: 'Aprovados',
        CANCELADO: 'Cancelados',
        EXCLUÍDO: 'Excluídos',
        REJEITADO: 'Rejeitados',
        RECALCULAR: 'Recalcular',
        FATURADO: 'Faturados',
        'EM ANÁLISE': 'Em Análise',
        PENDENTE: 'Pendentes',
        'SEM STATUS': 'Sem Status',
      };
      return labelMap[status] || this.capitalize(status);
    },
    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },
    getStatusStyle(status) {
      const styles = {
        DIGITADO: { icon: 'EditIcon', color: '#6366f1', bgColor: 'rgba(99, 102, 241, 0.1)' },
        INTEGRADO: { icon: 'CheckCircleIcon', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
        APROVADO: { icon: 'ThumbsUpIcon', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)' },
        CANCELADO: { icon: 'XCircleIcon', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
        EXCLUÍDO: { icon: 'TrashIcon', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
        REJEITADO: { icon: 'AlertCircleIcon', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' },
        RECALCULAR: { icon: 'RefreshCwIcon', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
        FATURADO: { icon: 'FileTextIcon', color: '#14b8a6', bgColor: 'rgba(20, 184, 166, 0.1)' },
        PENDENTE: { icon: 'ClockIcon', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
      };
      return styles[status] || { icon: 'PackageIcon', color: '#64748b', bgColor: 'rgba(100, 116, 139, 0.1)' };
    },
    toggleStatus(status) {
      if (this.selectedStatus === status) {
        this.$emit('filter', null);
      } else {
        this.$emit('filter', status);
      }
    },
  },
};
</script>

<style scoped>
.kpi-cards-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.kpi-card:hover {
  border-color: #6366f1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
}

.kpi-card.is-active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.04);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
}

.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
}

.kpi-content {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.kpi-count {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary, #1e293b);
  line-height: 1.2;
}

.kpi-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

@media (max-width: 1024px) {
  .kpi-cards-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .kpi-cards-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .kpi-card {
    padding: 0.5rem;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
  }

  .kpi-count {
    font-size: 1rem;
  }
}

body.dark-layout .kpi-card {
  background: var(--dark-card-bg, #283046);
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .kpi-card:hover {
  border-color: var(--primary, #7c3aed);
}

body.dark-layout .kpi-card.is-active {
  background: rgba(124, 58, 237, 0.1);
}

body.dark-layout .kpi-count {
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .kpi-label {
  color: var(--dark-text-secondary, #b4b7bd);
}
</style>
